/**
 * PoolScopeFields — the "which cycle or track does this pool live in" controls.
 *
 * Shared by CreateItemPoolModal (scope at birth) and the Item Pool Editor's
 * assign banner (scope before lock). Doc 03 §2.11A: scope is required at lock;
 * a draft may be unscoped. `allowNone` offers that explicit "not yet" choice.
 * Pure over props — the callers own the stores and the resolve step.
 */
import { useMemo } from 'react';
import { Select } from '../ds';
import {
  buildPoolScopeOptions,
  parsePoolScopeKey,
  poolScopeKeyForCycle,
  poolScopeKeyForTrack,
  tracksForCycle,
  POOL_SCOPE_NONE,
  POOL_SCOPE_NONE_AVAILABLE_MESSAGE,
  POOL_SCOPE_NONE_LABEL,
  type PoolScopeCycle,
  type PoolScopeKey,
  type PoolScopeTrack,
} from '../../lib/poolScope';

export interface PoolScopeFieldsProps {
  cycles: readonly PoolScopeCycle[];
  tracks: readonly PoolScopeTrack[];
  scopeKey: PoolScopeKey;
  trackId: string;
  onScopeKeyChange: (key: PoolScopeKey) => void;
  onTrackIdChange: (id: string) => void;
  /** Offer the explicit "Not yet" choice (an unscoped draft). */
  allowNone?: boolean;
  disabled?: boolean;
  idPrefix?: string;
}

export function PoolScopeFields({
  cycles,
  tracks,
  scopeKey,
  trackId,
  onScopeKeyChange,
  onTrackIdChange,
  allowNone = false,
  disabled = false,
  idPrefix = 'pool-scope',
}: PoolScopeFieldsProps) {
  const options = useMemo(() => buildPoolScopeOptions(cycles, tracks), [cycles, tracks]);
  const cycleTracks = useMemo(() => {
    const parsed = parsePoolScopeKey(scopeKey);
    return parsed?.kind === 'cycle' ? tracksForCycle(tracks, parsed.id) : [];
  }, [scopeKey, tracks]);
  const nothingToScopeTo = options.cycles.length === 0 && options.standaloneTracks.length === 0;
  const selectDisabled = disabled || (nothingToScopeTo && !allowNone);

  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-scope`} className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
          Cycle or track <span className="text-[var(--coral)]">*</span>
        </label>
        <Select
          id={`${idPrefix}-scope`}
          value={scopeKey}
          onChange={(e) => onScopeKeyChange(e.target.value as PoolScopeKey)}
          className="w-full"
          disabled={selectDisabled}
        >
          <option value="">{nothingToScopeTo && !allowNone ? 'No open cycle or track' : 'Choose where this pool lives…'}</option>
          {options.cycles.length > 0 && (
            <optgroup label="Cycles">
              {options.cycles.map((c) => (
                <option key={c.id} value={poolScopeKeyForCycle(c.id)}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          )}
          {options.standaloneTracks.length > 0 && (
            <optgroup label="Standalone tracks">
              {options.standaloneTracks.map((t) => (
                <option key={t.id} value={poolScopeKeyForTrack(t.id)}>
                  {t.name}
                </option>
              ))}
            </optgroup>
          )}
          {allowNone && <option value={POOL_SCOPE_NONE}>{POOL_SCOPE_NONE_LABEL}</option>}
        </Select>
        <p className="mt-1 text-xs text-[var(--text-ter)]">
          {scopeKey === POOL_SCOPE_NONE
            ? 'You can add and arrange items now. Assign a cycle or track before locking or ranking.'
            : nothingToScopeTo
              ? POOL_SCOPE_NONE_AVAILABLE_MESSAGE
              : 'The pool is the comparison set for rankings in this cycle or track.'}
        </p>
      </div>

      {cycleTracks.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}-track`} className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
            Track <span className="text-[var(--coral)]">*</span>
          </label>
          <Select
            id={`${idPrefix}-track`}
            value={trackId}
            onChange={(e) => onTrackIdChange(e.target.value)}
            className="w-full"
            disabled={disabled}
          >
            {cycleTracks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
      )}
    </>
  );
}
